// PRD §7.6 — founder mentor inbox. Replaces the old "Founder Network"
// placeholder. Lists missions belonging to this company, mentor interests
// per mission, with accept/decline. §13.10: never expose Vishwas score
// to founder; only the 1-line rationale.
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Loader2, Check, X, AlertCircle, Plus, Users, Clock, Lightbulb } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";
import { fp, useCohort } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Interest {
  id: number; status: string; expressedAt: string; statusDrifted: boolean;
  mentor: { id: number; fullName: string; headline: string | null; expertiseTags: string[]; linkedinUrl: string | null } | null;
  rationale: string | null;
}
interface MissionGroup {
  mission: { id: number; title: string; status: string; deadline: string | null };
  interests: Interest[];
}
interface PendingConfirmation {
  engagementId: number;
  missionId: number;
  missionTitle: string;
  mentor: { id: number; fullName: string; headline: string | null } | null;
  ageDays: number;
}
// PRD §7.4.4 — mentor-proposed missions awaiting founder review.
interface ProposedMission {
  id: number;
  mentor: { id: number; fullName: string; headline: string | null; expertiseTags: string[] };
  mentorTitle: string;
  mentorProblem: string;
  mentorApproach: string;
  virenRefinedTitle: string | null;
  virenRefinedProblem: string | null;
  virenRefinedDeliverable: string | null;
  virenRefinedWhatGoodLooksLike: string | null;
  virenRationale: string | null;
  founderStatus: string;
  declineReason: string | null;
  convertedMissionId: number | null;
  createdAt: string;
}

export default function FounderMentors() {
  const cohort = useCohort();
  const [data, setData] = useState<{ missions: MissionGroup[]; pendingConfirmations?: PendingConfirmation[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", problemStatement: "", deliverable: "", whatGoodLooksLike: "", domainTags: "" });
  const [creating, setCreating] = useState(false);
  const [proposed, setProposed] = useState<ProposedMission[]>([]);
  const [proposedErr, setProposedErr] = useState<string | null>(null);
  const [proposedBusyId, setProposedBusyId] = useState<number | null>(null);
  const [proposedDeclineFor, setProposedDeclineFor] = useState<number | null>(null);
  const [proposedDeclineReason, setProposedDeclineReason] = useState("");
  const [showRawFor, setShowRawFor] = useState<number | null>(null);

  function refresh() {
    fetch(`${BASE}api/founder-portal/mentor-inbox`, { headers: auth() })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData).catch(e => setErr(String(e)));
    // SHIPPING_CHECKLIST I9 — never silently swallow. If the proposed-
    // missions endpoint fails, surface a banner so the founder doesn't
    // think "no proposals" when really "we couldn't ask".
    fetch(`${BASE}api/founder-portal/proposed-missions`, { headers: auth() })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(d => { setProposed(d.proposals.filter((p: ProposedMission) => p.founderStatus === "awaiting")); setProposedErr(null); })
      .catch(e => setProposedErr(String(e)));
  }
  useEffect(refresh, []);

  async function respondProposal(id: number, decision: "accept" | "decline", reason?: string) {
    setProposedBusyId(id);
    try {
      const r = await fetch(`${BASE}api/founder-portal/proposed-missions/${id}/${decision}`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(decision === "decline" ? { reason: reason || "" } : {}),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Failed"); return; }
      if (decision === "accept") {
        alert(d.interestAutoAttached
          ? "Accepted. The mentor is now in your interests list for this mission."
          : "Accepted. The mission is live, but the mentor is no longer active so we couldn't auto-attach them.");
      }
      setProposedDeclineFor(null); setProposedDeclineReason("");
      refresh();
    } finally { setProposedBusyId(null); }
  }

  async function respond(interestId: number, decision: "accept" | "decline", reason?: string) {
    setBusyId(interestId);
    try {
      const r = await fetch(`${BASE}api/founder-portal/mentor-inbox/${interestId}/respond`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ decision, reason }),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Failed"); return; }
      if (d.message) alert(d.message);
      refresh();
    } finally { setBusyId(null); }
  }

  async function createMission(e: React.FormEvent) {
    e.preventDefault(); if (creating) return;
    setCreating(true);
    try {
      const r = await fetch(`${BASE}api/founder-portal/missions`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          ...form,
          domainTags: form.domainTags.split(",").map(t => t.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Failed"); return; }
      setShowCreate(false);
      setForm({ title: "", problemStatement: "", deliverable: "", whatGoodLooksLike: "", domainTags: "" });
      refresh();
    } finally { setCreating(false); }
  }

  if (err) return <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-red-400">{err}</div>;
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-10"><ProgressLoader active={true} title="Loading" stages={["Loading your missions", "Pulling mentor interests"]} /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Mentor Connect</div>
          <h1 className="text-2xl font-bold tracking-tight">Mentor interests on your missions</h1>
          <p className="text-sm text-white/65 mt-1">Senior operators interested in helping you. You decide who you talk to.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-sm font-semibold rounded-md px-3.5 py-2" data-testid="button-create-mission">
          <Plus className="w-3.5 h-3.5" /> New mission
        </button>
      </div>

      {/* PRD §13 — never let a "completed_pending_founder" engagement go
          silent. Banker auto-confirm cron flips it after 7d; we want the
          founder to act before that, not learn about it from a stale UI. */}
      {data.pendingConfirmations && data.pendingConfirmations.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="pending-confirmations">
          {data.pendingConfirmations.map((p) => {
            const remaining = Math.max(0, 7 - p.ageDays);
            return (
              <div key={p.engagementId} className="bg-[#FF5C00]/10 border border-[#FF5C00]/30 rounded-md px-4 py-3 flex items-start gap-2 text-[12px] text-amber-100" data-testid={`confirm-callout-${p.engagementId}`}>
                <AlertCircle className="w-4 h-4 mt-0.5 text-[#FF5C00] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-amber-50">
                    Action needed: confirm completion on "{p.missionTitle}"
                  </div>
                  <div className="text-amber-100/80 mt-0.5">
                    {p.mentor?.fullName || "The mentor"} marked this engagement complete {p.ageDays === 0 ? "today" : `${p.ageDays} day${p.ageDays === 1 ? "" : "s"} ago`}.
                    {remaining > 0
                      ? <> Open the workspace to review and accept or push back — auto-confirm in <strong>{remaining} day{remaining === 1 ? "" : "s"}</strong>.</>
                      : <> The system will auto-confirm on the next hourly run if you don't respond.</>}
                  </div>
                  <a href={`${BASE}1to10/founder/engagements/${p.engagementId}`}
                    className="inline-block mt-1.5 text-[#FF5C00] underline hover:no-underline font-medium"
                    data-testid={`confirm-link-${p.engagementId}`}>
                    Review and confirm →
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PRD §7.4.4 — mentor-proposed missions awaiting founder gate. Shown
          above existing missions because each one is a yes/no decision the
          founder owes the mentor. Viren has already filtered for fit; only
          'refined' rows reach here (rejected ones are hidden by the API). */}
      {proposedErr && (
        <div className="mb-5 rounded-md border border-red-500/35 bg-red-500/10 px-3 py-2 text-[12px] text-red-200" data-testid="text-proposed-fetch-error">
          Couldn't load mentor proposals — {proposedErr}. Mentors may have proposals waiting; refresh in a minute or contact PC.
        </div>
      )}
      {proposed.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="proposed-missions-section">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-amber-300">
            <Lightbulb className="w-3.5 h-3.5" />
            Mission proposals from mentors
          </div>
          {proposed.map((p) => (
            <div key={p.id} className="bg-[#FF5C00]/[0.06] border border-[#FF5C00]/25 rounded-lg p-4" data-testid={`proposed-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold">{p.virenRefinedTitle || p.mentorTitle}</div>
                  <div className="text-[11px] text-white/60 mt-0.5">
                    Proposed by <Link href={fp(cohort.slug, `mentors/${p.mentor.id}`)} className="text-white/85 font-medium underline hover:text-white" data-testid={`link-mentor-${p.mentor.id}`}>{p.mentor.fullName}</Link>
                    {p.mentor.headline && <> · {p.mentor.headline}</>}
                  </div>
                  {p.virenRefinedProblem && (
                    <div className="mt-2 text-xs text-white/75">
                      <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">Problem</span>
                      <div className="mt-0.5">{p.virenRefinedProblem}</div>
                    </div>
                  )}
                  {p.virenRefinedDeliverable && (
                    <div className="mt-2 text-xs text-white/75">
                      <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">Deliverable</span>
                      <div className="mt-0.5">{p.virenRefinedDeliverable}</div>
                    </div>
                  )}
                  {p.virenRefinedWhatGoodLooksLike && (
                    <div className="mt-2 text-xs text-white/75">
                      <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">What good looks like</span>
                      <div className="mt-0.5">{p.virenRefinedWhatGoodLooksLike}</div>
                    </div>
                  )}
                  {p.virenRationale && (
                    <div className="mt-2 text-[11px] text-amber-200/85 italic">Viren's take: {p.virenRationale}</div>
                  )}

                  {/* S2: anti-fabrication — show what the mentor actually
                      wrote, not just Viren's reshape. Collapsed by default
                      to keep the card scannable; one click to compare. */}
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowRawFor(showRawFor === p.id ? null : p.id)}
                      className="text-[11px] text-white/65 underline hover:text-white"
                      data-testid={`button-toggle-mentor-raw-${p.id}`}
                    >
                      {showRawFor === p.id ? "Hide" : "Show"} the mentor's original wording
                    </button>
                    {showRawFor === p.id && (
                      <div className="mt-2 space-y-2 rounded-md border border-white/[0.08] bg-black/30 p-3 text-[11px] text-white/75" data-testid={`mentor-raw-${p.id}`}>
                        <div>
                          <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">Mentor's title</span>
                          <div className="mt-0.5">{p.mentorTitle}</div>
                        </div>
                        <div>
                          <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">Mentor's problem</span>
                          <div className="mt-0.5 whitespace-pre-wrap">{p.mentorProblem}</div>
                        </div>
                        <div>
                          <span className="text-white/60 uppercase tracking-wider text-[10px] font-mono">Mentor's proposed approach</span>
                          <div className="mt-0.5 whitespace-pre-wrap">{p.mentorApproach}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {proposedDeclineFor === p.id ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={proposedDeclineReason}
                    onChange={(e) => setProposedDeclineReason(e.target.value)}
                    rows={2} maxLength={500}
                    placeholder="Optional: tell the mentor why (they see this)."
                    className="w-full bg-black/40 border border-white/15 rounded-md px-3 py-2 text-xs outline-none focus:border-[#FF5C00]/50 resize-none"
                    data-testid={`input-proposed-decline-reason-${p.id}`}
                  />
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => { setProposedDeclineFor(null); setProposedDeclineReason(""); }}
                      disabled={proposedBusyId === p.id}
                      className="text-xs text-white/65 hover:text-white border border-white/15 rounded px-2 py-1 disabled:opacity-40"
                      data-testid={`button-proposed-decline-cancel-${p.id}`}
                    >Cancel</button>
                    <button
                      onClick={() => respondProposal(p.id, "decline", proposedDeclineReason)}
                      disabled={proposedBusyId === p.id}
                      className="text-xs bg-red-500/85 hover:bg-red-500 text-white font-semibold rounded px-3 py-1 disabled:opacity-40"
                      data-testid={`button-proposed-decline-confirm-${p.id}`}
                    >{proposedBusyId === p.id ? "Declining..." : "Send decline"}</button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => { setProposedDeclineFor(p.id); setProposedDeclineReason(""); }}
                    disabled={proposedBusyId === p.id}
                    className="text-xs text-white/70 hover:text-white border border-white/15 rounded px-2 py-1 disabled:opacity-40"
                    data-testid={`button-proposed-decline-${p.id}`}
                  >Decline</button>
                  <button
                    onClick={() => respondProposal(p.id, "accept")}
                    disabled={proposedBusyId === p.id}
                    className="text-xs bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white font-semibold rounded px-3 py-1 disabled:opacity-40 flex items-center gap-1"
                    data-testid={`button-proposed-accept-${p.id}`}
                  >
                    {proposedBusyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Accept and add to my missions
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data.missions.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-8 text-center text-sm text-white/60">
          You haven't created any missions yet. Click "New mission" to describe what you need help with.
        </div>
      ) : (
        <div className="space-y-5">
          {data.missions.map((g) => (
            <div key={g.mission.id} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-5">
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-base font-bold">{g.mission.title}</h3>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300">{g.mission.status}</span>
              </div>
              <p className="text-xs text-white/60 mb-3">{g.interests.length} mentor{g.interests.length === 1 ? "" : "s"} interested</p>

              {/* PRD §7.6.1 §3 — comparison framing when 2+ live mentors on the
                  same mission. Without this, the founder picks the first card by
                  decision fatigue instead of comparing. */}
              {(() => {
                const live = g.interests.filter(i => i.status === "pending_founder" || i.status === "reconfirmed");
                if (live.length < 2) return null;
                return (
                  <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-md bg-[#FF5C00]/10 border border-[#FF5C00]/25 text-[11px] text-amber-200" data-testid={`compare-banner-${g.mission.id}`}>
                    <Users className="w-3.5 h-3.5 mt-0.5 text-[#FF5C00] flex-shrink-0" />
                    <span>
                      <strong>{live.length} mentors</strong> are interested. Compare their backgrounds before deciding — talking to all of them is the norm, not the exception.
                    </span>
                  </div>
                );
              })()}

              {/* PRD §7.6.2 — SLA reminder banner when the oldest pending interest
                  is approaching its 5/7-day auto-release threshold. Visible nudge,
                  not a blocking modal — the founder is in control. */}
              {(() => {
                const live = g.interests.filter(i => i.status === "pending_founder" || i.status === "reconfirmed");
                if (live.length === 0) return null;
                const slaDays = g.mission.status === "draft_pending_founder" ? 5 : 7;
                const oldest = live.reduce((acc, i) => {
                  const t = new Date(i.expressedAt).getTime();
                  return t < acc ? t : acc;
                }, Date.now());
                const ageDays = Math.floor((Date.now() - oldest) / (1000 * 60 * 60 * 24));
                const remaining = slaDays - ageDays;
                if (remaining > 2) return null;
                const tone = remaining <= 0
                  ? "bg-red-500/10 border-red-500/35 text-red-200"
                  : "bg-amber-500/10 border-amber-500/30 text-amber-200";
                return (
                  <div className={`flex items-start gap-2 mb-3 px-3 py-2 rounded-md border text-[11px] ${tone}`} data-testid={`sla-banner-${g.mission.id}`}>
                    <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {remaining > 0
                        ? <>Auto-released in <strong>{remaining} day{remaining === 1 ? "" : "s"}</strong>. Respond above to keep these conversations open.</>
                        : <>Past the {slaDays}-day SLA — the system will release these interests on tonight's run.</>}
                    </span>
                  </div>
                );
              })()}

              {g.interests.length === 0 ? (
                <div className="text-xs text-white/60">No interest yet — we'll notify you when a mentor expresses interest.</div>
              ) : (
                <div className="space-y-2">
                  {g.interests.map((i) => (
                    <div key={i.id} className="bg-black/30 border border-white/[0.08] rounded-md p-3" data-testid={`interest-card-${i.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{i.mentor?.fullName || "Mentor"}</div>
                          {i.mentor?.headline && <div className="text-xs text-white/60">{i.mentor.headline}</div>}
                          {i.rationale && <div className="text-xs text-white/70 mt-1">Why this fit: {i.rationale}</div>}
                          {i.statusDrifted && (
                            <div className="flex items-start gap-1 mt-2 text-[11px] text-amber-300">
                              <AlertCircle className="w-3 h-3 mt-0.5" />
                              Mission has changed since they expressed interest. Accepting will ask them to re-confirm before scheduling.
                            </div>
                          )}
                        </div>
                        {(i.status === "pending_founder" || i.status === "reconfirmed") && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button onClick={() => respond(i.id, "decline")} disabled={busyId === i.id}
                              className="text-xs text-white/70 hover:text-white border border-white/15 rounded px-2 py-1 disabled:opacity-40"
                              data-testid={`button-decline-${i.id}`}>
                              Decline
                            </button>
                            <button onClick={() => respond(i.id, "accept")} disabled={busyId === i.id}
                              className="text-xs bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white font-semibold rounded px-3 py-1 disabled:opacity-40 flex items-center gap-1"
                              data-testid={`button-accept-${i.id}`}>
                              {busyId === i.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Accept
                            </button>
                          </div>
                        )}
                        {i.status !== "pending_founder" && i.status !== "reconfirmed" && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300 flex-shrink-0">{i.status}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
          <div className="bg-card border border-white/10 rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold">Create a mission</h3>
              <button onClick={() => setShowCreate(false)} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={createMission} className="space-y-3">
              <Field label="Title" v={form.title} onC={v => setForm({ ...form, title: v })} testId="input-mission-title" />
              <Field label="Problem statement" v={form.problemStatement} onC={v => setForm({ ...form, problemStatement: v })} multi testId="input-mission-problem" />
              <Field label="Deliverable" v={form.deliverable} onC={v => setForm({ ...form, deliverable: v })} multi testId="input-mission-deliverable" />
              <Field label="What good looks like" v={form.whatGoodLooksLike} onC={v => setForm({ ...form, whatGoodLooksLike: v })} multi testId="input-mission-success" />
              <Field label="Domain tags (comma-separated)" v={form.domainTags} onC={v => setForm({ ...form, domainTags: v })} testId="input-mission-tags" />
              <button type="submit" disabled={creating || !form.title || !form.problemStatement || !form.deliverable || !form.whatGoodLooksLike}
                className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-sm font-semibold rounded-md py-2 flex items-center justify-center gap-2"
                data-testid="button-mission-submit">
                {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{creating ? "Creating" : "Publish mission"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, onC, multi, testId }: { label: string; v: string; onC: (v: string) => void; multi?: boolean; testId: string }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/60 mb-1">{label}</div>
      {multi ? (
        <textarea value={v} onChange={e => onC(e.target.value)} rows={3} className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF5C00]/50 resize-none" data-testid={testId} />
      ) : (
        <input value={v} onChange={e => onC(e.target.value)} className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF5C00]/50" data-testid={testId} />
      )}
    </label>
  );
}
